import React from 'react'
import styled from 'styled-components'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { createModal, useModalize } from '@/components/modalize/mui'
import { breakpoints, colors } from '@/styles'
import { Button } from '@/components/button'
import CloseButton from '@/components/close-button'
import ToneRadioInput from './radio-input'
import SparkleSVG from './../sparkle.svg?react'
import { showGenerateTextModal, showUpdateGenerateTextModal } from '../generate-text'
import { SectionWrapper, Title, Description } from '../elements/modal'
import { ChangeToneProps } from '../types'

const Wrapper = styled.div``

const StyledSVG = styled(SparkleSVG)`
  margin-right: 12px;
`

export const FormLabel = styled.p`
  display: flex;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${colors.black};
  margin: 0;
`

const ToneOption = styled.div`
  display: flex;
  margin: 16px 0;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
  }
`

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  background-color: ${colors.grey96};
  width: 100%;
  padding: 16px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    position: absolute;
    bottom: 0;
  }
`

export const StyledButton = styled(Button)`
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

export const ErrorText = styled.p<{ top?: number; right?: number; bottom?: number; left?: number }>`
  font-family: 'Inter';
  color: ${colors.red64};
  font-size: 12px;
  line-height: 16px;
  margin: 0;
  width: 100%;
  text-align: left;
  margin-top: ${props => props.top}px;
  margin-right: ${props => props.right}px;
  margin-bottom: ${props => props.bottom}px;
  margin-left: ${props => props.left}px;
`

const Required = styled.span`
  color: ${colors.red64};
  line-height: 24px;
`

const ChangeTone: React.FC<ChangeToneProps> = ({
  listingId,
  heading,
  formValues = {},
  description = '',
  initAction = 'update',
  setDescriptionFormValue
}) => {
  const { close } = useModalize()
  const { t } = useTranslation()
  const buttonKey = initAction === 'update' ? 'tone' : 'gentext'

  const handleGenerate = (values: any) => {
    close()

    if (initAction === 'update') {
      showUpdateGenerateTextModal({
        listingId: listingId,
        description: description,
        tone: values?.description_tone,
        heading: heading ?? t('l.smart_description.change_tone.change_tone.title', 'Change tone'),
        setDescriptionFormValue: setDescriptionFormValue,
        eventId: 'application-description-ai-tone-generate-click'
      })
    } else {
      showGenerateTextModal({
        listingId: listingId,
        formValues: { ...formValues, tone: values?.description_tone },
        heading: heading ?? t('l.smart_description.change_tone.change_tone.title', 'Change tone'),
        setDescriptionFormValue: setDescriptionFormValue,
        eventId: 'application-description-ai-gentext-generate-click'
      })
    }
  }

  const validationSchema = Yup.object().shape({
    description_tone: Yup.string().required(t('common.required', 'Required'))
  })

  const initialFormValues = initAction === 'update' ? {} : { description_tone: 'professional' }

  return (
    <Wrapper>
      <SectionWrapper>
        <Title>
          <StyledSVG />
          {heading ?? t('l.smart_description.change_tone.change_tone.title', 'Change tone')}
        </Title>
        <CloseButton onClick={close} />
      </SectionWrapper>
      <Formik
        initialValues={initialFormValues}
        onSubmit={handleGenerate}
        validationSchema={validationSchema}
      >
        {({ errors }) => {
          return (
            <Form>
              <SectionWrapper>
                <FormLabel>
                  {t(
                    'l.smart_description.change_tone.what_tone.label',
                    'What tone would you like your description to have?'
                  )}
                  <Required>*</Required>
                </FormLabel>
                <ToneOption>
                  <ToneRadioInput
                    id={`application-description-ai-${buttonKey}-professional`}
                    name="description_tone"
                    value="professional"
                  >
                    {t('l.smart_description.change_tone.professional.label', 'Professional')}
                  </ToneRadioInput>
                  <ToneRadioInput
                    id={`application-description-ai-${buttonKey}-friendly`}
                    name="description_tone"
                    value="friendly"
                  >
                    {t('l.smart_description.change_tone.friendly.label', 'Friendly')}
                  </ToneRadioInput>
                  <ToneRadioInput
                    id={`application-description-ai-${buttonKey}-casual`}
                    name="description_tone"
                    value="casual"
                  >
                    {t('l.smart_description.change_tone.casual.label', 'Casual')}
                  </ToneRadioInput>
                </ToneOption>
                {errors?.description_tone && (
                  <ErrorText top={12} bottom={12}>
                    {errors?.description_tone}
                  </ErrorText>
                )}
                <Description>
                  {t(
                    'l.smart_description.change_tone.description.text',
                    `The description of your listing will be generated based on the details you’ve provided. Any
                missing or unavailable information will not be included.`
                  )}
                </Description>
              </SectionWrapper>
              <ButtonRow>
                <StyledButton
                  id={`application-description-ai-${buttonKey}-generate-click`}
                  size="medium"
                  theme="primary"
                  type="submit"
                >
                  {t('l.smart_description.change_tone.button.text', 'Generate')}
                </StyledButton>
              </ButtonRow>
            </Form>
          )
        }}
      </Formik>
    </Wrapper>
  )
}

export default ChangeTone

export const show = createModal(ChangeTone, { width: '640px' })
